import React, { useRef } from "react";
import {
  Upload,
  FileText,
  X,
  User,
  Phone,
  Wallet,
} from "lucide-react";
import AppButton from "../../../components/common/appButton/AppButton";

const CustomerPreferences = ({
  data,
  updateData,
  onNext,
  onBack,
}) => {
  const fileInputRef = useRef(null);

  const amenities = [
    "Pool",
    "Gym",
    "Parking",
    "Balcony",
    "Garden",
    "Security",
    "Pet-friendly",
    "Smart Home",
  ];

  const toggleAmenity = (amenity) => {
    const current = data.amenities || [];

    const updated = current.includes(amenity)
      ? current.filter((item) => item !== amenity)
      : [...current, amenity];

    updateData({
      amenities: updated,
    });
  };

  /* =====================================================
     UAE PHONE FORMAT
  ===================================================== */

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    let digits = value.replace(/\D/g, "");

    if (digits.startsWith("971")) {
      digits = digits.slice(3);
    }

    digits = digits.slice(0, 9);

    let formatted = "+971";

    if (digits.length > 0) {
      formatted += ` ${digits.slice(0, 2)}`;
    }

    if (digits.length > 2) {
      formatted += ` ${digits.slice(2, 5)}`;
    }

    if (digits.length > 5) {
      formatted += ` ${digits.slice(5, 9)}`;
    }

    updateData({
      phone_number: formatted,
    });
  };

  /* =====================================================
     EMIRATES ID FILE
  ===================================================== */

  const handleEmiratesIdChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    updateData({
      emirates_id: file,
    });
  };

  const removeEmiratesId = () => {
    updateData({
      emirates_id: null,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-[#6a7847]">
        Required information
      </div>

      <h1 className="font-['Playfair_Display'] text-[30px] leading-[1.08] tracking-[-0.035em] text-[#1f2318] sm:text-[34px]">
        Complete your{" "}
        <span className="italic font-medium text-[#4c7a2e]">
          profile.
        </span>
      </h1>

      <p className="mt-2 text-[11px] leading-5 text-[#92968e]">
        Tell us a little more about you to complete your
        investor registration.
      </p>

      <div className="mt-6 space-y-5">

        {/* =====================================================
            PERSONAL INFORMATION
        ===================================================== */}

        <div>
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#edf3e8] text-[#4c7a2e]">
              <User size={12} />
            </div>

            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#596257]">
              Personal Information
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

            {/* First Name */}

            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                First Name
                <span className="ml-1 text-[#4c7a2e]">
                  *
                </span>
              </label>

              <input
                type="text"
                value={data?.first_name || ""}
                onChange={(e) =>
                  updateData({
                    first_name: e.target.value,
                  })
                }
                placeholder="Enter first name"
                className="
                  h-[45px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#e1e5dd]
                  bg-white
                  px-3.5
                  text-[11px]
                  text-[#1f2318]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-4
                  focus:ring-[#4c7a2e]/[0.07]
                "
              />
            </div>

            {/* Last Name */}

            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                Last Name
                <span className="ml-1 text-[#4c7a2e]">
                  *
                </span>
              </label>

              <input
                type="text"
                value={data?.last_name || ""}
                onChange={(e) =>
                  updateData({
                    last_name: e.target.value,
                  })
                }
                placeholder="Enter last name"
                className="
                  h-[45px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#e1e5dd]
                  bg-white
                  px-3.5
                  text-[11px]
                  text-[#1f2318]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-4
                  focus:ring-[#4c7a2e]/[0.07]
                "
              />
            </div>

          </div>
        </div>

        {/* =====================================================
            PHONE NUMBER
        ===================================================== */}

        <div>
          <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
            <Phone size={11} className="text-[#6a7847]" />
            Phone Number
            <span className="text-[#4c7a2e]">*</span>
          </label>

          <input
            type="tel"
            value={data?.phone_number || ""}
            onChange={handlePhoneChange}
            placeholder="+971 50 000 0000"
            className="
              h-[45px]
              w-full
              rounded-[10px]
              border
              border-[#e1e5dd]
              bg-white
              px-3.5
              text-[11px]
              text-[#1f2318]
              outline-none
              transition
              placeholder:text-[#a5aaa3]
              focus:border-[#7da458]
              focus:ring-4
              focus:ring-[#4c7a2e]/[0.07]
            "
          />
        </div>

        {/* =====================================================
            WALLET ADDRESS
        ===================================================== */}

        <div>
          <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
            <Wallet size={11} className="text-[#6a7847]" />
            Wallet Address
            <span className="text-[#4c7a2e]">*</span>
          </label>

          <input
            type="text"
            value={data?.wallet_address || ""}
            onChange={(e) =>
              updateData({
                wallet_address: e.target.value,
              })
            }
            placeholder="Enter your wallet address"
            className="
              h-[45px]
              w-full
              rounded-[10px]
              border
              border-[#e1e5dd]
              bg-white
              px-3.5
              text-[11px]
              text-[#1f2318]
              outline-none
              transition
              placeholder:text-[#a5aaa3]
              focus:border-[#7da458]
              focus:ring-4
              focus:ring-[#4c7a2e]/[0.07]
            "
          />

          <p className="mt-1.5 text-[8px] leading-4 text-[#9a9f96]">
            Enter the wallet address you will use for your
            investments.
          </p>
        </div>

        {/* =====================================================
            EMIRATES ID
        ===================================================== */}

        <div>

          <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
            <FileText
              size={11}
              className="text-[#6a7847]"
            />

            Emirates ID

            <span className="text-[#4c7a2e]">*</span>
          </label>

          {!data?.emirates_id ? (
            <button
              type="button"
              onClick={() =>
                fileInputRef.current?.click()
              }
              className="
                flex
                min-h-[92px]
                w-full
                flex-col
                items-center
                justify-center
                rounded-[12px]
                border
                border-dashed
                border-[#cfd9c8]
                bg-[#fafcf8]
                px-4
                text-center
                transition
                hover:border-[#7da458]
                hover:bg-[#f6faf2]
              "
            >
              <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf2e3] text-[#4c7a2e]">
                <Upload size={14} />
              </div>

              <p className="text-[10px] font-semibold text-[#596257]">
                Upload Emirates ID
              </p>

              <p className="mt-1 text-[8px] text-[#9a9f96]">
                PDF, JPG or PNG · Max 10MB
              </p>
            </button>
          ) : (
            <div className="flex items-center justify-between rounded-[12px] border border-[#dce7d5] bg-[#f7faf4] px-3.5 py-3">

              <div className="flex min-w-0 items-center gap-3">

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6f0df] text-[#4c7a2e]">
                  <FileText size={15} />
                </div>

                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold text-[#37452f]">
                    {data.emirates_id.name}
                  </p>

                  <p className="mt-0.5 text-[8px] text-[#92968e]">
                    {(
                      data.emirates_id.size /
                      (1024 * 1024)
                    ).toFixed(2)}{" "}
                    MB
                  </p>
                </div>

              </div>

              <button
                type="button"
                onClick={removeEmiratesId}
                className="ml-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[#8c9388] shadow-sm transition hover:text-[#b45850]"
              >
                <X size={13} />
              </button>

            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleEmiratesIdChange}
          />

        </div>

        {/* =====================================================
            INVESTMENT PREFERENCES
        ===================================================== */}

        <div>

          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#596257]">
              Investment Preferences
            </p>

            <p className="mt-1 text-[9px] text-[#9a9f96]">
              Help us understand what you're looking for.
            </p>
          </div>

          <div className="space-y-4">

            {/* Budget */}

            <SelectField
              label="Budget Range"
              value={data?.budget || ""}
              placeholder="Select your budget"
              options={[
                "Under AED 500K",
                "AED 500K - AED 1M",
                "AED 1M - AED 2M",
                "AED 2M+",
              ]}
              onChange={(value) =>
                updateData({
                  budget: value,
                })
              }
            />

            {/* Move in */}

            <SelectField
              label="Move-in Timeline"
              value={data?.moveInTimeline || ""}
              placeholder="When are you planning to move?"
              options={[
                "Immediately",
                "Within 3 months",
                "3 - 6 months",
                "6+ months",
              ]}
              onChange={(value) =>
                updateData({
                  moveInTimeline: value,
                })
              }
            />

            {/* Locations */}

            <div>
              <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                Preferred Locations
              </label>

              <input
                value={data?.preferredLocations || ""}
                onChange={(e) =>
                  updateData({
                    preferredLocations:
                      e.target.value,
                  })
                }
                placeholder="e.g. Dubai Marina, Downtown Dubai"
                className="
                  h-[45px]
                  w-full
                  rounded-[10px]
                  border
                  border-[#e1e5dd]
                  bg-white
                  px-3.5
                  text-[11px]
                  text-[#1f2318]
                  outline-none
                  transition
                  placeholder:text-[#a5aaa3]
                  focus:border-[#7da458]
                  focus:ring-4
                  focus:ring-[#4c7a2e]/[0.07]
                "
              />
            </div>

          </div>
        </div>

        {/* =====================================================
            AMENITIES
        ===================================================== */}

        {/* 
        <div>
          <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
            Must-have Amenities
          </label>

          <div className="flex flex-wrap gap-2">
            {amenities.map((amenity) => {
              const selected =
                data.amenities?.includes(amenity);

              return (
                <button
                  key={amenity}
                  type="button"
                  onClick={() =>
                    toggleAmenity(amenity)
                  }
                  className={`
                    rounded-full
                    border
                    px-3
                    py-1.5
                    text-[9px]
                    transition
                    ${
                      selected
                        ? "border-[#4c7a2e] bg-[#4c7a2e] text-white"
                        : "border-[#dfe5da] bg-white text-[#67736c] hover:border-[#7da458]"
                    }
                  `}
                >
                  {amenity}
                </button>
              );
            })}
          </div>
        </div>
        */}

      </div>

      {/* =====================================================
          BUTTONS
      ===================================================== */}

      <div className="mt-6 flex gap-3">

        <AppButton
          variant="secondary"
          onClick={onBack}
        >
          ← Back
        </AppButton>

        <AppButton onClick={onNext}>
          Continue →
        </AppButton>

      </div>

    </div>
  );
};


/* =====================================================
   SELECT FIELD
===================================================== */

const SelectField = ({
  label,
  value,
  placeholder,
  options,
  onChange,
}) => {
  return (
    <div>

      <label className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
        {label}
      </label>

      <select
        value={value}
        onChange={(e) =>
          onChange(e.target.value)
        }
        className="
          h-[45px]
          w-full
          rounded-[10px]
          border
          border-[#e1e5dd]
          bg-white
          px-3.5
          text-[11px]
          text-[#1f2318]
          outline-none
          transition
          focus:border-[#7da458]
          focus:ring-4
          focus:ring-[#4c7a2e]/[0.07]
        "
      >
        <option value="">
          {placeholder}
        </option>

        {options.map((option) => (
          <option
            key={option}
            value={option}
          >
            {option}
          </option>
        ))}
      </select>

    </div>
  );
};

export default CustomerPreferences;